import { invoiceKeys } from '@/features/invoices/keys';
import { useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AuthError, createAuthenticatedClient } from "./client";

export function useAuthenticatedClient() {
  const { getAccessToken } = useAuth();

  const getClient = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) {
      // Surface as AuthError so the QueryCache onError handler runs the shared
      // silent-refresh-with-retry recovery flow. Don't call loginRedirect here
      // — on flaky mobile networks a single failed refresh would redirect the
      // user every time, even when retrying would succeed.
      throw new AuthError(401, "Failed to acquire access token");
    }
    return createAuthenticatedClient(token);
  }, [getAccessToken]);

  return getClient;
}

export const queryKeys = {
  dashboardSummary: {
    all: ["dashboard-summary"] as const,
    list: (params?: unknown) => ["dashboard-summary", params] as const,
  },
  analytics: {
    all: ["analytics"] as const,
    summary: (params: unknown) => ["analytics", "summary", params] as const,
    breakdown: (params: unknown) => ["analytics", "breakdown", params] as const,
    orders: (params: unknown) => ["analytics", "orders", params] as const,
  },
  projects: {
    all: ["projects"] as const,
    list: (params?: unknown) => ["projects", "list", params] as const,
    detail: (id: string) => ["projects", "detail", id] as const,
    apCosts: (id: string) => ["projects", "ap-costs", id] as const,
    timeline: (id: string, params?: unknown) =>
      ["projects", "timeline", id, params] as const,
    globalTimeline: (params?: unknown) =>
      ["projects", "global-timeline", params] as const,
    actions: (id: string) => ["projects", "actions", id] as const,
    withActions: (params?: unknown) =>
      ["projects", "with-actions", params] as const,
    withoutProjectActions: (params?: unknown) =>
      ["projects", "without-project-actions", params] as const,
    actionTypes: ["projects", "action-types"] as const,
    shareLinks: (id: string) => ["projects", "share-links", id] as const,
    automationHistory: (id: string) =>
      ["projects", "automation-history", id] as const,
    deliveryTimeline: (id: string) =>
      ["projects", "delivery-timeline", id] as const,
    deliveryTimelines: (ids: string[]) =>
      ["projects", "delivery-timelines", ids] as const,
    cancellationPreview: (id: string) =>
      ["projects", "cancellation-preview", id] as const,
  },
  dropshipQuoteDrafts: {
    all: ["dropship-quote-drafts"] as const,
    list: (params?: unknown) =>
      ["dropship-quote-drafts", "list", params] as const,
    detail: (id: string) => ["dropship-quote-drafts", "detail", id] as const,
    events: (id: string) => ["dropship-quote-drafts", "events", id] as const,
  },
  dropshipV2: {
    all: ["dropship-v2"] as const,
    capabilities: ["dropship-v2", "capabilities"] as const,
    shell: (id: string) => ["dropship-v2", "shell", id] as const,
    fields: (id: string) => ["dropship-v2", "fields", id] as const,
    lineGroups: (id: string) => ["dropship-v2", "line-groups", id] as const,
    issues: (id: string) => ["dropship-v2", "issues", id] as const,
    clarifications: (id: string) =>
      ["dropship-v2", "clarifications", id] as const,
    freightReview: (id: string) =>
      ["dropship-v2", "freight-review", id] as const,
    deliveryTiming: (id: string) =>
      ["dropship-v2", "delivery-timing", id] as const,
    audit: (id: string) => ["dropship-v2", "audit", id] as const,
    workQueue: (params?: unknown) =>
      ["dropship-v2", "work-queue", params] as const,
    readinessMetrics: (windowDays: number) =>
      ["dropship-v2", "readiness-metrics", windowDays] as const,
    plan: (id: string, planId?: number | null) =>
      planId == null
        ? (["dropship-v2", "plan", id] as const)
        : (["dropship-v2", "plan", id, planId] as const),
    poPreview: (id: string, proposedPOId: string) =>
      ["dropship-v2", "po-preview", id, proposedPOId] as const,
    placement: (id: string, attemptId: string) =>
      ["dropship-v2", "placement", id, attemptId] as const,
  },
  orders: {
    all: ["orders"] as const,
    list: (params?: unknown) => ["orders", "list", params] as const,
    detail: (id: string) => ["orders", "detail", id] as const,
  },
  purchaseOrders: {
    all: ["purchase-orders"] as const,
    list: (params?: unknown) => ["purchase-orders", "list", params] as const,
    detail: (id: string) => ["purchase-orders", "detail", id] as const,
    automationHistory: (id: string) =>
      ["purchase-orders", "automation-history", id] as const,
    freightBookingHistory: (id: string) =>
      ["purchase-orders", "freight-booking-history", id] as const,
    carrierOptions: (id: string, shipmentId: number) =>
      ["purchase-orders", "carrier-options", id, shipmentId] as const,
    deliveryTimeline: (id: string) =>
      ["purchase-orders", "delivery-timeline", id] as const,
    invoiceActions: (id: string) =>
      ["purchase-orders", "invoice-actions", id] as const,
    globalActions: ["purchase-orders", "global-actions"] as const,
  },
  apInvoices: invoiceKeys,
  shipments: {
    all: ["shipments"] as const,
    list: (params?: unknown) => ["shipments", "list", params] as const,
    detail: (id: string) => ["shipments", "detail", id] as const,
  },
  blockedCarriers: {
    all: ["blocked-carriers"] as const,
  },
  companySettings: {
    all: ["company-settings"] as const,
  },
  warehouse: {
    all: ["warehouse"] as const,
    warehouses: ["warehouse", "warehouses"] as const,
    items: (params?: unknown) => ["warehouse", "items", params] as const,
    item: (productUuid: string, condition: string, grade: string | null) =>
      ["warehouse", "item", productUuid, condition, grade] as const,
    stock: (uuid: string) => ["warehouse", "stock-detail", uuid] as const,
    productStock: (productUuid: string) => ["warehouse", "product-stock", productUuid] as const,
  },
  customerGroups: {
    all: ["customer-groups"] as const,
    list: (params?: unknown) => ["customer-groups", "list", params] as const,
    detail: (id: string) => ["customer-groups", "detail", id] as const,
  },
  customers: {
    all: ["customers"] as const,
    list: (params?: unknown) => ["customers", "list", params] as const,
  },
  qualityTiers: {
    all: ["quality-tiers"] as const,
    categories: ["quality-tiers", "categories"] as const,
    category: (nodeId: number) => ["quality-tiers", "category", nodeId] as const,
    comments: (nodeId: number, manufacturerUuid: string) =>
      ["quality-tiers", "comments", nodeId, manufacturerUuid] as const,
  },
  manufacturers: {
    all: ["manufacturers"] as const,
    list: (params?: unknown) => ["manufacturers", "list", params] as const,
    detail: (id: string) => ["manufacturers", "detail", id] as const,
    qualityTiers: (id: string) => ["manufacturers", "quality-tiers", id] as const,
    allQualityTiers: ["manufacturers", "quality-tiers"] as const,
    policyDocs: (id: string) => ["manufacturers", "policy-docs", id] as const,
    cardFees: (id: string) => ["manufacturers", "card-fees", id] as const,
    competitorPrices: (id: string, kind: string, page: number) =>
      ["manufacturers", "competitor-prices", id, kind, page] as const,
  },
  specials: {
    all: ["specials"] as const,
    list: (params?: unknown) => ["specials", "list", params] as const,
    products: (key: string, params?: unknown) => ["specials", "products", key, params] as const,
  },
  contacts: {
    all: ["contacts"] as const,
    list: (params?: unknown) => ["contacts", "list", params] as const,
    detail: (id: number) => ["contacts", "detail", id] as const,
  },
  products: {
    all: ["products"] as const,
    list: (params?: unknown) => ["products", "list", params] as const,
    detail: (id: string) => ["products", "detail", id] as const,
    quotable: (q: string, limit: number) => ["products", "quotable", q, limit] as const,
    shippingFacts: (id: string) => ["products", "shipping-facts", id] as const,
    facts: (id: string) => ["products", "facts", id] as const,
    factHistory: (id: string, key: string) => ["products", "fact-history", id, key] as const,
    discontinuation: (id: string) => ["products", "discontinuation", id] as const,
    pricingDetail: (id: string) => ["products", "pricing-detail", id] as const,
    specialNetHistory: (id: string) => ["products", "special-net-history", id] as const,
    priceHistory: (id: string, from: string) => ["products", "price-history", id, from] as const,
    configAccessoryV4: (id: string) => ["products", "config-accessory-v4", id] as const,
    options: (id: string) => ["products", "options", id] as const,
    suggestions: (uuid: string) => ["products", "suggestions", uuid] as const,
  },
  communications: {
    all: ["communications"] as const,
    threads: (params?: unknown) =>
      ["communications", "threads", params] as const,
    thread: (uuid: string) => ["communications", "thread", uuid] as const,
    byPO: (poUuid: string) => ["communications", "by-po", poUuid] as const,
    byProject: (projectUuid: string) =>
      ["communications", "by-project", projectUuid] as const,
  },
  aiAgents: {
    all: ["ai-agents"] as const,
    list: () => ["ai-agents", "list"] as const,
    detail: (key: string) => ["ai-agents", "detail", key] as const,
  },
  apiKeys: {
    all: ["api-keys"] as const,
    list: () => ["api-keys", "list"] as const,
  },
  staff: {
    all: ["staff"] as const,
    me: ["staff", "me"] as const,
    list: (params?: unknown) => ["staff", "list", params] as const,
    detail: (id: string) => ["staff", "detail", id] as const,
    projects: (id: string) => ["staff", "projects", id] as const,
  },
  projectRoleDefaults: {
    all: ["project-role-defaults"] as const,
  },
  comments: {
    all: ["comments"] as const,
    list: (projectUuid: string) => ["comments", "list", projectUuid] as const,
  },
  mentions: {
    search: (q: string, projectUuid?: string) =>
      ["mentions", "search", q, projectUuid] as const,
  },
  customerComms: {
    all: ["customer-comms"] as const,
    list: (params?: unknown) => ["customer-comms", "list", params] as const,
    detail: (id: number) => ["customer-comms", "detail", id] as const,
    stats: ["customer-comms", "stats"] as const,
    actions: (params?: unknown) =>
      ["customer-comms", "actions", params] as const,
    byProject: (projectId: number) =>
      ["customer-comms", "by-project", projectId] as const,
    byOrder: (orderId: number) =>
      ["customer-comms", "by-order", orderId] as const,
    byCustomerGroup: (groupId: number) =>
      ["customer-comms", "by-customer-group", groupId] as const,
  },
  activity: {
    all: ["activity"] as const,
  },
  globalSearch: {
    query: (q: string) => ["global-search", q] as const,
  },
  e2eTesting: {
    all: ["e2e-testing"] as const,
    scenarios: ["e2e-testing", "scenarios"] as const,
    status: ["e2e-testing", "status"] as const,
  },
} as const;
